import type { ReactNode } from 'react';
import { SearchXIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface NotFoundStateProps extends React.ComponentProps<'div'> {
  /** What could not be found, e.g. "Workspace not found". */
  title: string;
  /** Why, in plain language, and what the reader can do about it. */
  description: string;
  /** The way back. Always offer one — a dead end is not a recovery surface. */
  action?: ReactNode;
  /** The mark above the title; a lock for a workspace that exists but is closed to you. */
  icon?: LucideIcon;
  className?: string;
}

/**
 * Shared recovery surface for every not-found case: unknown address,
 * unresolvable workspace, unresolvable workflow.
 *
 * One implementation used everywhere rather than a per-feature variant — there
 * is no variation between them to justify a seam.
 */
export function NotFoundState({
  title,
  description,
  action,
  icon: Icon = SearchXIcon,
  className,
  ...props
}: NotFoundStateProps) {
  return (
    <section
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center',
        className,
      )}
      {...props}
    >
      <Icon
        aria-hidden="true"
        className="text-muted-foreground size-6"
        strokeWidth={1.5}
      />
      <h1 className="text-foreground text-xl font-medium">{title}</h1>
      <p className="text-muted-foreground max-w-prose text-sm">{description}</p>
      {action ? <div className="mt-2">{action}</div> : null}
    </section>
  );
}
